import { useState, useEffect, useCallback, useRef, type ChangeEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, X, Sparkle, FileText, Lock, Star, Download } from "lucide-react";
import { api, getAccessToken, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useLookup } from "../context/LookupContext.js";
import { useToast } from "../context/ToastContext.js";
import { uploadDocumentWithAI, uploadDocumentOnly, AIBadge, SmartBitRejection, ExtractionFailed } from "../lib/resumeUpload.js";
import { VirtualHrGuide } from "../components/VirtualHrGuide.js";
import { useVirtualHrGuide } from "../hooks/useVirtualHrGuide.js";
import { useVirtualHr } from "../lib/virtualHr.js";
import { INTRO, stepLine, SIGN_DOCUMENTS_LINE, docAccepted, rejectionLine, wrongFileTypeLine, FORM11_RECEIVED, UPLOAD_FAILED, READ_FAILED, type VerifiedDoc } from "../lib/onboardingScript.js";
import type { SayInput } from "../hooks/useVirtualHrGuide.js";
import { AddressAutocompleteInput, type ResolvedAddress } from "../lib/addressAutocomplete.js";
import { PhoneInput } from "../lib/PhoneInput.js";
import { DUPLICATE_PHONE_MESSAGE, firstDuplicatePhoneIndex, validateEmergencyContactPhones } from "../lib/emergencyContactRules.js";
import { DatePicker } from "../components/DatePicker.js";
import { PolicySignPanel, SignedPolicyDownloads } from "../components/PolicySignPanel.js";
import { EMPTY_POLICY_STATUS, type PolicyKind, type PolicySigningStatus, type SignedPolicy } from "../lib/policySigning.js";
import { DocVersionHistory } from "../components/DocVersionHistory.js";
import { DocumentLightbox, type LightboxTarget } from "../components/DocumentLightbox.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { Button } from "../components/ui/button.js";
import { SpecularButton } from "../components/ui/specular-button.js";
import { SelectField, type SelectFieldProps } from "../components/ui/select-field.js";
import { FileInput } from "../components/ui/file-input.js";
import { DataConsentNotice } from "../components/onboarding/DataConsentNotice.js";
import { DocumentLockedNotice } from "../components/onboarding/DocumentLockedNotice.js";
import { DocumentSlot, type SlotDoc } from "../components/onboarding/DocumentSlot.js";
import { FILE_RULES, wrongFileTypeMessage, type FileRuleKey } from "../lib/fileTypes.js";
import { ContactVerificationBadge } from "../components/onboarding/ContactVerificationBadge.js";
import { OnboardingStepper } from "../components/onboarding/OnboardingStepper.js";
import { Badge } from "../components/ui/badge.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import {
  ReferencesEditor, referencesPayload, validateReferences, withReferenceSlots, type Reference,
} from "../components/onboarding/ReferencesEditor.js";

// Thrown by a step's own validation once it has already shown the reason, so
// the save handler's catch leaves that message alone.
const VALIDATION_HANDLED = "validation";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WizardState {
  // Office country, read from the server once at hydrate. "IN" (the common
  // case) shows the Degree / Marksheet / Form 11 upload cards on step 5;
  // anything else hides them.
  country: string;
  // Step 1
  title: string; dateOfBirth: string; gender: string; maritalStatus: string;
  spouseName: string; anniversaryDate: string; personalEmail: string;
  personalPhone: string; linkedIn: string; tshirtSize: string;
  // Step 2
  panNumber: string; aadhaarNumber: string;
  firstName: string; middleName: string; lastName: string;
  // Step 3
  residentialAddressLine1: string; residentialAddressLine2: string;
  residentialPincode: string; residentialCity: string;
  residentialState: string; residentialDistrict: string;
  residentialLat: number | null; residentialLng: number | null;
  sameAsPermanent: boolean;
  permanentAddressLine1: string; permanentAddressLine2: string;
  permanentPincode: string; permanentCity: string;
  permanentState: string; permanentDistrict: string;
  permanentLat: number | null; permanentLng: number | null;
  // Step 4
  contacts: Contact[];
  // Step 5
  bankAccountNumber: string; bankIfscCode: string;
  bankName: string; bankBranchName: string; bankAccountHolderName: string;
  // Step 6
  education: Education[]; workHistory: WorkHistoryItem[]; skills: string;
  references: Reference[];
  // Step 7 — Equipment
  equipmentItems: EquipmentItem[]; equipmentStartDate: string; equipmentOfficeLocation: string;
}

interface Contact {
  name: string; relationship: string; phone: string; email: string; isPrimary: boolean;
  /** HR's verification call, read-only here; absent on a contact not saved yet. */
  verificationStatus?: string;
}
interface Education {
  degree: string; institution: string; fieldOfStudy: string; startYear: string; endYear: string;
}
interface WorkHistoryItem {
  company: string; title: string; startDate: string; endDate: string; isCurrent: boolean;
}
interface EquipmentItem {
  category: string; specs: string;
}

const EMPTY_CONTACT: Contact = { name: "", relationship: "PARENT", phone: "", email: "", isPrimary: false };
const EMPTY_EDU: Education = { degree: "", institution: "", fieldOfStudy: "", startYear: "", endYear: "" };
const EMPTY_WORK: WorkHistoryItem = { company: "", title: "", startDate: "", endDate: "", isCurrent: false };
const EMPTY_EQUIP: EquipmentItem = { category: "LAPTOP", specs: "" };

export const INITIAL: WizardState = {
  country: "",
  title: "", dateOfBirth: "", gender: "", maritalStatus: "", spouseName: "",
  anniversaryDate: "", personalEmail: "", personalPhone: "", linkedIn: "", tshirtSize: "",
  panNumber: "", aadhaarNumber: "",
  firstName: "", middleName: "", lastName: "",
  residentialAddressLine1: "", residentialAddressLine2: "",
  residentialPincode: "", residentialCity: "", residentialState: "", residentialDistrict: "",
  residentialLat: null, residentialLng: null,
  sameAsPermanent: false,
  permanentAddressLine1: "", permanentAddressLine2: "",
  permanentPincode: "", permanentCity: "", permanentState: "", permanentDistrict: "",
  permanentLat: null, permanentLng: null,
  contacts: [{ ...EMPTY_CONTACT, isPrimary: true }, { ...EMPTY_CONTACT }],
  bankAccountNumber: "", bankIfscCode: "", bankName: "", bankBranchName: "", bankAccountHolderName: "",
  education: [{ ...EMPTY_EDU }], workHistory: [{ ...EMPTY_WORK }], skills: "",
  references: withReferenceSlots([]),
  // HRMS-0636: default kit is injected by buildStepPayload at submit; the
  // optional specifics panel starts empty.
  equipmentItems: [], equipmentStartDate: "", equipmentOfficeLocation: "",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function decodeToken(token: string): { sub: string; employeeId: string; role: string } {
  try {
    const p = JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>;
    return {
      sub: String(p["sub"] ?? ""),
      employeeId: String(p["employeeId"] ?? p["sub"] ?? ""),
      role: String(p["role"] ?? ""),
    };
  } catch { return { sub: "", employeeId: "", role: "" }; }
}

async function lookupPincode(pin: string): Promise<{ city: string; state: string; district: string } | null> {
  try {
    const res = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
    const [data] = await res.json() as [{ Status: string; PostOffice: Array<{ District: string; State: string; Block: string }> }];
    if (data.Status === "Success" && data.PostOffice?.length) {
      const po = data.PostOffice[0]!;
      return { city: po.Block || po.District, state: po.State, district: po.District };
    }
  } catch (err) { logError("OnboardingWizardPage.lookupPincode", err); }
  return null;
}

// Consent + verified-document lock state, owned by the page and shared by the
// steps that upload Aadhaar, PAN and bank documents.
interface DocGate {
  consentAt: string | null;
  onConsented: (at: string) => void;
  /** Latest status per document type, e.g. { PAN: "VERIFIED" }. */
  docStatus: Record<string, string>;
  /** The file on record per document type, so a field can show it with edit and delete icons. */
  docFiles: Record<string, SlotDoc>;
  onUploaded: () => void;
  /** Sirra says (and captions) a line. */
  say: (line: SayInput) => void;
  /** True while a document is being checked, so Sirra shows she is thinking. */
  busy: (working: boolean) => void;
}

const NOTHING = (): void => {};

// Sirra speaks her intro, and each step's line, once per browser tab ("intro", "step0", "step1"...).
// A refresh of a step she already spoke only shows the line; the employee presses Replay to hear it.
// sessionStorage, so a refresh keeps it but every new tab starts fresh and she speaks again.
const heardKey = (employeeId: string) => `sirra_heard_${employeeId}`;
function readHeard(employeeId: string): Set<string> {
  try { return new Set(JSON.parse(sessionStorage.getItem(heardKey(employeeId)) ?? "[]") as string[]); } catch { return new Set(); }
}
function writeHeard(employeeId: string, keys: string[]): void {
  try { sessionStorage.setItem(heardKey(employeeId), JSON.stringify([...readHeard(employeeId), ...keys])); } catch { /* ignore */ }
}

// What she says after an upload, fetched ahead on the Documents step so her reply is there when the check ends.
const UPLOAD_RESULT_LINES = [
  docAccepted("aadhaar"), docAccepted("pan"), docAccepted("bank"), docAccepted("resume"), docAccepted("degree"), docAccepted("marksheet"),
  UPLOAD_FAILED, READ_FAILED, wrongFileTypeLine("scan"), wrongFileTypeLine("resume"), wrongFileTypeLine("pdfOrImage"),
];

const CONSENT_FIRST = "Accept the data-protection notice above first";

// The server refuses uploads with 409 (verified, locked) or 412 (no consent);
// show its message instead of the generic AI-extraction fallback.
function uploadBlockedMessage(err: unknown): string | null {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 409 || status === 412 ? (errMsg(err) ?? "This upload is not allowed right now.") : null;
}

// ─── Shared UI atoms ──────────────────────────────────────────────────────────

const inp: React.CSSProperties = {
  width: "100%", padding: "10px 12px", border: "1.5px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: 14, background: "var(--color-surface-0)",
  outline: "none", boxSizing: "border-box",
};
const lbl: React.CSSProperties = { display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4, color: "var(--color-surface-700)" };
const fieldWrap: React.CSSProperties = { marginBottom: 16 };
const row2: React.CSSProperties = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 };
const sectionCard: React.CSSProperties = {
  background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-surface-200)",
  padding: 24, marginBottom: 20,
};
const hrCard: React.CSSProperties = {
  ...sectionCard, background: "var(--color-surface-50)",
  borderColor: "var(--color-surface-300)", opacity: 0.8,
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div style={fieldWrap}>
      <label style={lbl}>{label}{required && <span style={{ color: "var(--color-error)", marginLeft: 2 }}>*</span>}</label>
      {children}
    </div>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} style={{ ...inp, ...(props.style ?? {}) }} />;
}
function Select(props: SelectFieldProps) {
  return <SelectField {...props} />;
}

// ─── Step Components ──────────────────────────────────────────────────────────

function StepPersonal({ data, onChange, gate }: { data: WizardState; onChange: (k: keyof WizardState, v: unknown) => void; gate: DocGate }) {
  const f = (k: keyof WizardState) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => onChange(k, e.target.value);
  const titles = useLookup("EMPLOYEE_TITLE");
  const genders = useLookup("GENDER");
  const maritalStatuses = useLookup("MARITAL_STATUS");
  const tshirtSizes = useLookup("TSHIRT_SIZE");
  // HRMS-0628: PAN and Aadhaar numbers are AI-extracted on step 1 (Documents)
  // and now shown here for review. Numbers HR has already verified against
  // their doc stay read-only (same lock as before).
  const aadhaarLocked = gate.docStatus["AADHAAR"] === "VERIFIED";
  const panLocked = gate.docStatus["PAN"] === "VERIFIED";
  const panValid = !data.panNumber || /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(data.panNumber);
  const aadhaarValid = !data.aadhaarNumber || /^\d{12}$/.test(data.aadhaarNumber);
  return (
    <div>
      <div style={row2}>
        <Field label="First Name"><Input value={data.firstName} onChange={e => onChange("firstName", e.target.value)} placeholder="First name" /></Field>
        <Field label="Last Name"><Input value={data.lastName} onChange={e => onChange("lastName", e.target.value)} placeholder="Last name" /></Field>
      </div>
      <div style={row2}>
        <Field label="Middle Name"><Input value={data.middleName} onChange={e => onChange("middleName", e.target.value)} placeholder="Optional" /></Field>
        <Field label="Title">
          <Select value={data.title} onChange={f("title")}>
            <option value="">Select</option>
            {titles.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
          </Select>
        </Field>
      </div>
      <div style={row2}>
        <Field label="Aadhaar Number (12 digits)" required>
          <Input value={data.aadhaarNumber} onChange={e => onChange("aadhaarNumber", e.target.value.replace(/\D/g, ""))}
            maxLength={12} placeholder="12-digit Aadhaar number" readOnly={aadhaarLocked}
            style={{ borderColor: aadhaarValid ? undefined : "var(--color-error)" }} />
          {!aadhaarValid && <span style={{ fontSize: 12, color: "var(--color-error)" }}>Must be exactly 12 digits</span>}
        </Field>
        <Field label="PAN Number" required>
          <Input value={data.panNumber} onChange={e => onChange("panNumber", e.target.value.toUpperCase())}
            maxLength={10} placeholder="e.g. ABCDE1234F" readOnly={panLocked}
            style={{ borderColor: panValid ? undefined : "var(--color-error)" }} />
          {!panValid && <span style={{ fontSize: 12, color: "var(--color-error)" }}>Format: AAAAA9999A</span>}
        </Field>
      </div>
      <div style={row2}>
        <Field label="Date of Birth" required>
          <DatePicker value={data.dateOfBirth} onChange={v => onChange("dateOfBirth", v)} max={new Date(Date.now() - 18 * 365.25 * 86400000).toISOString().slice(0, 10)} />
        </Field>
        <Field label="Gender">
          <Select value={data.gender} onChange={f("gender")}>
            <option value="">Select</option>
            {genders.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
          </Select>
        </Field>
      </div>
      <div style={row2}>
        <Field label="Marital Status">
          <Select value={data.maritalStatus} onChange={f("maritalStatus")}>
            <option value="">Select</option>
            {maritalStatuses.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
          </Select>
        </Field>
        <Field label="T-Shirt Size">
          <Select value={data.tshirtSize} onChange={f("tshirtSize")}>
            <option value="">Select</option>
            {tshirtSizes.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
          </Select>
        </Field>
      </div>
      {data.maritalStatus === "MARRIED" && (
        <div style={row2}>
          <Field label="Spouse Name"><Input value={data.spouseName} onChange={f("spouseName")} placeholder="Full name" /></Field>
          <Field label="Anniversary Date"><DatePicker value={data.anniversaryDate} onChange={v => onChange("anniversaryDate", v)} /></Field>
        </div>
      )}
      <div style={row2}>
        <Field label="Personal Email"><Input type="email" value={data.personalEmail} onChange={f("personalEmail")} placeholder="personal@example.com" /></Field>
        <Field label="Personal Phone"><PhoneInput value={data.personalPhone} onChange={(v) => onChange("personalPhone", v)} /></Field>
      </div>
      <Field label="LinkedIn URL"><Input type="url" value={data.linkedIn} onChange={f("linkedIn")} placeholder="https://linkedin.com/in/..." /></Field>
    </div>
  );
}

// Collects every onboarding document on the first wizard step. AI parse
// results flow into the shared WizardState via `onChange`, so later steps
// (Personal Info, Bank Details, References & History) land already filled in.
// Non-US hires also upload Degree, Marksheet and (optionally) Form 11 here —
// the three "Education & statutory documents" cards HRMS-0627 added to the
// Resume step moved up alongside the rest.
function StepDocuments({
  data, onChange, employeeId, gate,
}: { data: WizardState; onChange: (k: keyof WizardState, v: unknown) => void; employeeId: string; gate: DocGate }) {
  const aadhaarLocked = gate.docStatus["AADHAAR"] === "VERIFIED";
  const panLocked = gate.docStatus["PAN"] === "VERIFIED";
  const bankLocked = gate.docStatus["BANK_DOCUMENT"] === "VERIFIED";
  const [aadhaarLoading, setAadhaarLoading] = useState(false);
  const [panLoading, setPanLoading] = useState(false);
  const [bankLoading, setBankLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [aadhaarMsg, setAadhaarMsg] = useState("");
  const [panMsg, setPanMsg] = useState("");
  const [bankMsg, setBankMsg] = useState("");
  const [resumeMsg, setResumeMsg] = useState("");
  // HR uploads the offer letter; the employee only downloads it.
  const [offerDoc, setOfferDoc] = useState<{ id: string; name: string } | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    api.get<Array<{ id: string; name: string }>>(`/employees/${employeeId}/documents`, { params: { type: "OFFER_LETTER" } })
      .then(({ data }) => { if (!cancelled) setOfferDoc(data[0] ?? null); })
      .catch((err) => { logError("OnboardingWizardPage.loadOfferLetter", err); if (!cancelled) setOfferDoc(null); });
    return () => { cancelled = true; };
  }, [employeeId]);

  async function downloadOfferLetter() {
    if (!offerDoc) return;
    // Open the tab inside the click so popup blockers allow it; the signed URL arrives after.
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    try {
      const { data } = await api.get<{ url: string }>(`/documents/${offerDoc.id}/url`);
      if (tab) tab.location.href = data.url; else window.location.assign(data.url);
    } catch (err) {
      tab?.close();
      logError("OnboardingWizardPage.downloadOfferLetter", err);
    }
  }

  const vhr = useVirtualHr();
  const checking = aadhaarLoading || panLoading || bankLoading || resumeLoading;
  useEffect(() => { gate.busy(checking); }, [checking]); // eslint-disable-line react-hooks/exhaustive-deps

  // A refusal from the document check is spoken and shown as it is; anything else keeps its fallback text.
  // `doc` is the slot the file went into, so what Sirra says names the right documents.
  const failMsg = (err: unknown, readFallback: string, doc?: VerifiedDoc): string => {
    if (err instanceof SmartBitRejection) {
      gate.say(rejectionLine(err.verdict.code, { doc, detected: err.verdict.detectedType }));
      return `✕ ${err.message}`;
    }
    // Saved, but the details could not be read: show the server's reason when it gave one.
    if (err instanceof ExtractionFailed) {
      const res = (err.cause as { response?: { status?: number; data?: { details?: { code?: string; detectedType?: string } } } })?.response;
      const refusal = res?.status === 422 ? res.data?.details : undefined;
      // The server can also refuse the file at this stage: say what it said, not "couldn't read".
      gate.say(refusal?.code ? rejectionLine(refusal.code, { doc, detected: refusal.detectedType }) : READ_FAILED);
      return errMsg(err.cause) && res?.status === 422 ? `✕ ${errMsg(err.cause)}` : readFallback;
    }
    // The upload itself did not go through (storage unreachable, network, locked document...).
    gate.say(UPLOAD_FAILED);
    return `✕ ${uploadBlockedMessage(err) ?? "The upload failed. Please try again."}`;
  };

  // What a document gave is put in the form only where the form is still empty, so nothing the employee
  // (or HR) already entered is overwritten. The employee reviews and edits it on the later steps, and the
  // step-1 save keeps it. Each helper returns true when it filled something, for the "pre-filled" message.
  const fillDateOfBirth = (raw: unknown): boolean => {
    const dob = String(raw ?? "").trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob) || data.dateOfBirth) return false;
    onChange("dateOfBirth", dob);
    return true;
  };
  const fillGender = (raw: unknown): boolean => {
    const gender = String(raw ?? "").trim().toUpperCase(); // the GENDER lookup values are MALE / FEMALE
    if ((gender !== "MALE" && gender !== "FEMALE") || data.gender) return false;
    onChange("gender", gender);
    return true;
  };
  const fillResidentialAddress = (raw: unknown): boolean => {
    if (!raw || typeof raw !== "object" || data.residentialAddressLine1) return false;
    const a = raw as Record<string, unknown>;
    const text = (k: string) => (typeof a[k] === "string" ? (a[k] as string).trim() : "");
    const pincode = text("pincode").replace(/\D/g, "");
    const parts: Array<[keyof WizardState, string]> = [
      ["residentialAddressLine1", text("line1")], ["residentialAddressLine2", text("line2")],
      ["residentialPincode", /^\d{6}$/.test(pincode) ? pincode : ""],
      ["residentialCity", text("city")], ["residentialState", text("state")], ["residentialDistrict", text("district")],
    ];
    const present = parts.filter(([, v]) => v);
    present.forEach(([k, v]) => onChange(k, v));
    return present.length > 0;
  };
  const filledList = (items: string[]): string =>
    items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}` : (items[0] ?? "");

  const readAadhaar = async (file: File) => {
    setAadhaarLoading(true); setAadhaarMsg("");
    try {
      const fields = await uploadDocumentWithAI(employeeId, file, "AADHAAR", "AADHAAR", { context: "onboarding" });
      const filled: string[] = [];
      if (fields["aadhaarNumber"]) { onChange("aadhaarNumber", (fields["aadhaarNumber"] as string).replace(/\D/g, "")); filled.push("number"); }
      if (fields["name"]) {
        const parts = String(fields["name"]).trim().split(/\s+/).filter(Boolean);
        if (parts[0]) onChange("firstName", parts[0]);
        onChange("middleName", parts.length > 2 ? parts.slice(1, -1).join(" ") : "");
        if (parts.length > 1) onChange("lastName", parts[parts.length - 1]!);
        filled.push("name");
      }
      if (fillDateOfBirth(fields["dateOfBirth"])) filled.push("date of birth");
      if (fillGender(fields["gender"])) filled.push("gender");
      const addressFilled = fillResidentialAddress(fields["address"]);
      if (addressFilled) filled.push("address");
      setAadhaarMsg(filled.length
        ? `✓ Aadhaar scanned — ${filledList(filled)} pre-filled; review on Personal Info${addressFilled ? " and Address" : ""}.`
        : "Aadhaar uploaded, but no details could be read from it. Please enter them on Personal Info.");
      gate.say(docAccepted("aadhaar"));
    } catch (err) { logError("OnboardingWizardPage.handleAadhaar", err); setAadhaarMsg(failMsg(err, "Upload saved, but the details could not be read automatically. Please enter them on Personal Info.", "aadhaar")); }
    setAadhaarLoading(false);
    gate.onUploaded();
  };

  const readPan = async (file: File) => {
    setPanLoading(true); setPanMsg("");
    try {
      const fields = await uploadDocumentWithAI(employeeId, file, "PAN", "PAN", { context: "onboarding" });
      const filled: string[] = [];
      if (fields["panNumber"]) { onChange("panNumber", (fields["panNumber"] as string).toUpperCase()); filled.push("number"); }
      if (fields["name"] && !data.firstName) {
        const parts = String(fields["name"]).trim().split(/\s+/).filter(Boolean);
        if (parts[0]) onChange("firstName", parts[0]);
        if (parts.length > 1) onChange("lastName", parts[parts.length - 1]!);
        filled.push("name");
      }
      if (fillDateOfBirth(fields["dateOfBirth"])) filled.push("date of birth");
      setPanMsg(filled.length
        ? `✓ PAN scanned — ${filledList(filled)} pre-filled; review on Personal Info.`
        : "PAN uploaded, but no details could be read from it. Please enter the number on Personal Info.");
      gate.say(docAccepted("pan"));
    } catch (err) { logError("OnboardingWizardPage.handlePan", err); setPanMsg(failMsg(err, "Upload saved, but the PAN could not be read automatically. Please enter it on Personal Info.", "pan")); }
    setPanLoading(false);
    gate.onUploaded();
  };

  const readBank = async (file: File) => {
    setBankLoading(true); setBankMsg("");
    try {
      const fields = await uploadDocumentWithAI(employeeId, file, "BANK_DOCUMENT", "BANK_DOCUMENT", { context: "onboarding" });
      if (fields["accountNumber"]) onChange("bankAccountNumber", fields["accountNumber"]);
      if (fields["ifscCode"]) onChange("bankIfscCode", (fields["ifscCode"] as string).toUpperCase());
      if (fields["bankName"]) onChange("bankName", fields["bankName"]);
      if (fields["branchName"]) onChange("bankBranchName", fields["branchName"]);
      if (fields["accountHolderName"]) onChange("bankAccountHolderName", fields["accountHolderName"]);
      const anyRead = ["accountNumber", "ifscCode", "bankName", "branchName", "accountHolderName"].some(k => fields[k]);
      setBankMsg(anyRead
        ? "✓ Bank document scanned — details pre-filled; review on Bank Details."
        : "Bank document uploaded, but no details could be read from it. Please enter them on Bank Details.");
      gate.say(docAccepted("bank"));
    } catch (err) { logError("OnboardingWizardPage.handleBankDoc", err); setBankMsg(failMsg(err, "Upload saved, but the bank details could not be read automatically. Please enter them manually.", "bank")); }
    setBankLoading(false);
    gate.onUploaded();
  };

  const readResume = async (file: File) => {
    setResumeLoading(true); setResumeMsg("");
    try {
      const fields = await uploadDocumentWithAI(employeeId, file, "OTHER", "RESUME", { context: "onboarding" }) as Record<string, unknown>;
      const filled: string[] = [];
      // Contact details are taken only where still empty (HR may have filled them); name and date of birth
      // come from the Aadhaar / PAN, never from a résumé.
      if (fields["email"] && !data.personalEmail) { onChange("personalEmail", String(fields["email"]).trim()); filled.push("email"); }
      if (fields["phone"] && !data.personalPhone) { onChange("personalPhone", String(fields["phone"]).replace(/[\s()-]/g, "")); filled.push("phone"); }
      if (fields["linkedIn"] && !data.linkedIn) { onChange("linkedIn", String(fields["linkedIn"]).trim()); filled.push("LinkedIn"); }
      if (fields["skills"]) { onChange("skills", String(fields["skills"])); filled.push("skills"); }
      if (Array.isArray(fields["education"]) && fields["education"].length > 0) {
        filled.push("education");
        onChange("education", (fields["education"] as Array<Record<string, unknown>>).map(e => ({
          degree: String(e["degree"] ?? ""), institution: String(e["institution"] ?? ""),
          fieldOfStudy: String(e["fieldOfStudy"] ?? ""),
          startYear: String(e["startYear"] ?? ""), endYear: String(e["endYear"] ?? ""),
        })));
      }
      if (Array.isArray(fields["workHistory"]) && fields["workHistory"].length > 0) {
        filled.push("work history");
        onChange("workHistory", (fields["workHistory"] as Array<Record<string, unknown>>).map(w => ({
          company: String(w["company"] ?? ""), title: String(w["title"] ?? ""),
          startDate: String(w["startDate"] ?? ""), endDate: w["isCurrent"] ? "" : String(w["endDate"] ?? ""),
          isCurrent: Boolean(w["isCurrent"]),
        })));
      }
      setResumeMsg(filled.length
        ? `✓ Resume parsed — ${filledList(filled)} pre-filled; review on Personal Info and References & History.`
        : "Resume uploaded, but no details could be read from it. Please enter them on References & History.");
      gate.say(docAccepted("resume"));
    } catch (err) { logError("OnboardingWizardPage.handleResume", err); setResumeMsg(failMsg(err, "Upload saved, but your resume could not be read automatically. Please enter your details on References & History.", "resume")); }
    setResumeLoading(false);
    gate.onUploaded();
  };

  // Sirra off for this document (the Onboarding box, the master switch, or the document's own box in the
  // admin settings): the file is just stored. Nothing is checked or read, nothing is filled in, and Sirra says nothing.
  const storeOnly = async (file: File, docType: string, label: string, setLoading: (v: boolean) => void, setMsg: (v: string) => void) => {
    setLoading(true); setMsg("");
    try {
      await uploadDocumentOnly(employeeId, file, docType, { skipVerify: true, context: "onboarding" });
      setMsg(`✓ ${label} uploaded.`);
    } catch (err) { logError(`OnboardingWizardPage.storeOnly.${docType}`, err); setMsg(failMsg(err, "")); }
    setLoading(false);
    gate.onUploaded();
  };
  const sirraReads = (docType: string) => vhr.isDocEnabled(docType, "onboarding");
  // A file that is not the type the field says (e.g. a WebP in "JPG / PNG / PDF") is refused here, before
  // anything is uploaded, with a message under the field. Replacing a file comes through the same handlers.
  const typeAccepted = (file: File, rule: FileRuleKey, setMsg: (v: string) => void): boolean => {
    const wrong = wrongFileTypeMessage(file, FILE_RULES[rule]);
    if (wrong) { setMsg(`✕ ${wrong}`); gate.say(wrongFileTypeLine(rule)); } // the line says the same words aloud
    return !wrong;
  };
  const handleAadhaar = (file: File) => !typeAccepted(file, "scan", setAadhaarMsg) ? undefined
    : sirraReads("AADHAAR") ? readAadhaar(file) : storeOnly(file, "AADHAAR", "Aadhaar", setAadhaarLoading, setAadhaarMsg);
  const handlePan = (file: File) => !typeAccepted(file, "scan", setPanMsg) ? undefined
    : sirraReads("PAN") ? readPan(file) : storeOnly(file, "PAN", "PAN", setPanLoading, setPanMsg);
  const handleBank = (file: File) => !typeAccepted(file, "scan", setBankMsg) ? undefined
    : sirraReads("BANK_DOCUMENT") ? readBank(file) : storeOnly(file, "BANK_DOCUMENT", "Bank document", setBankLoading, setBankMsg);
  const handleResume = (file: File) => !typeAccepted(file, "resume", setResumeMsg) ? undefined
    : sirraReads("OTHER") ? readResume(file) : storeOnly(file, "OTHER", "Resume", setResumeLoading, setResumeMsg);

  const nonUS = data.country && data.country !== "US";
  const docMsg = (msg: string) => msg
    ? <p style={{ fontSize: 13, color: msg.startsWith("✓") ? "green" : "var(--color-warning)", marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>{msg.startsWith("✓") && <CheckCircle2 size={14} />}{msg.replace(/^[✓✕]\s*/, "")}</p>
    : null;

  return (
    <div>
      <DataConsentNotice employeeId={employeeId} consentedAt={gate.consentAt} onConsented={gate.onConsented} />

      <p style={{ fontSize: 13, color: "var(--color-surface-700)", marginBottom: 16 }}>
        Attach these documents and we&apos;ll fill the rest of the wizard from them. You can edit anything we get wrong before you submit.
      </p>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-[15px]">Offer Letter</CardTitle>
          <CardDescription>Shared by HR. Download it for your records — you don&apos;t need to upload anything here.</CardDescription>
        </CardHeader>
        <CardContent>
          {offerDoc === undefined
            ? <p className="text-[13px] text-surface-500">Loading…</p>
            : offerDoc
              ? <Button variant="outline" size="sm" onClick={() => void downloadOfferLetter()}><Download size={14} /> Download offer letter</Button>
              : <p className="text-[13px] text-surface-500">HR hasn&apos;t attached your offer letter yet.</p>}
        </CardContent>
      </Card>

      <div style={sectionCard}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Aadhaar Card</h3>
          <AIBadge loading={aadhaarLoading} />
        </div>
        {aadhaarLocked
          ? <DocumentLockedNotice />
          : <DocumentSlot doc={gate.docFiles["AADHAAR"]} onFile={handleAadhaar} onDeleted={() => { setAadhaarMsg(""); gate.onUploaded(); }} loading={aadhaarLoading}
              label="Upload Aadhaar front (JPG / PNG / PDF)" accept={FILE_RULES.scan.accept} disabled={!gate.consentAt} disabledReason={CONSENT_FIRST} />}
        {docMsg(aadhaarMsg)}
      </div>

      <div style={sectionCard}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>PAN Card</h3>
          <AIBadge loading={panLoading} />
        </div>
        {panLocked
          ? <DocumentLockedNotice />
          : <DocumentSlot doc={gate.docFiles["PAN"]} onFile={handlePan} onDeleted={() => { setPanMsg(""); gate.onUploaded(); }} loading={panLoading}
              label="Upload PAN card (JPG / PNG / PDF)" accept={FILE_RULES.scan.accept} disabled={!gate.consentAt} disabledReason={CONSENT_FIRST} />}
        {docMsg(panMsg)}
      </div>

      <div style={sectionCard}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Bank Document</h3>
          <AIBadge loading={bankLoading} />
        </div>
        <p style={{ fontSize: 13, color: "var(--color-surface-600)", margin: "0 0 10px" }}>
          Upload a cancelled cheque or first page of your passbook for payroll setup.
        </p>
        {bankLocked
          ? <DocumentLockedNotice />
          : <DocumentSlot doc={gate.docFiles["BANK_DOCUMENT"]} onFile={handleBank} onDeleted={() => { setBankMsg(""); gate.onUploaded(); }} loading={bankLoading}
              label="Upload cancelled cheque or passbook (JPG / PNG / PDF)" accept={FILE_RULES.scan.accept} disabled={!gate.consentAt} disabledReason={CONSENT_FIRST} />}
        {docMsg(bankMsg)}
      </div>

      <div style={sectionCard}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Resume</h3>
          <AIBadge loading={resumeLoading} />
        </div>
        <DocumentSlot doc={gate.docFiles["OTHER"]} onFile={handleResume} onDeleted={() => { setResumeMsg(""); gate.onUploaded(); }} loading={resumeLoading}
          label="Upload resume (PDF or DOCX)" accept={FILE_RULES.resume.accept} />
        {docMsg(resumeMsg)}
      </div>

      {/* HRMS-0627: India (and any non-US) hires also attach their degree,
          marksheet and (optionally) Form 11. US hires skip this block —
          those statutory docs are handled through ADP. The dev DB stores
          "India" and prod stores "IN", so match the server's
          "anything but US" convention. */}
      {nonUS && (
        <div style={sectionCard}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700 }}>Education &amp; statutory documents</h3>
          <p className="mb-3 text-xs text-surface-500">
            Degree and marksheet are required. Form 11 is optional — you can add it later from your profile.
          </p>
          <EduDocCard employeeId={employeeId} docType="DEGREE"    label="Degree certificate"       required docStatus={gate.docStatus} docFile={gate.docFiles["DEGREE"]}    onUploaded={gate.onUploaded} say={gate.say} />
          <EduDocCard employeeId={employeeId} docType="MARKSHEET" label="Marksheet"                required docStatus={gate.docStatus} docFile={gate.docFiles["MARKSHEET"]} onUploaded={gate.onUploaded} say={gate.say} />
          <EduDocCard employeeId={employeeId} docType="FORM_11"   label="Form 11 (EPF nomination)"          docStatus={gate.docStatus} docFile={gate.docFiles["FORM_11"]}   onUploaded={gate.onUploaded} say={gate.say} />
        </div>
      )}
    </div>
  );
}

function AddressBlock({
  prefix, data, onChange, onPincodeLookup,
}: {
  prefix: "residential" | "permanent"; data: WizardState;
  onChange: (k: keyof WizardState, v: unknown) => void;
  onPincodeLookup: (pin: string, target: "residential" | "permanent") => Promise<void>;
}) {
  const f = (sub: string) => `${prefix}${sub.charAt(0).toUpperCase()}${sub.slice(1)}` as keyof WizardState;
  const fStr = (sub: string) => String(data[f(sub)] ?? "");
  const [loading, setLoading] = useState(false);

  const handlePin = async (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    onChange(f("pincode") as keyof WizardState, val);
    if (val.length === 6) {
      setLoading(true);
      await onPincodeLookup(val, prefix);
      setLoading(false);
    }
  };

  const handleResolve = (addr: ResolvedAddress) => {
    onChange(f("addressLine1"), addr.addressLine1);
    if (addr.addressLine2) onChange(f("addressLine2"), addr.addressLine2);
    if (addr.pincode) onChange(f("pincode"), addr.pincode);
    if (addr.city) onChange(f("city"), addr.city);
    if (addr.state) onChange(f("state"), addr.state);
    if (addr.district) onChange(f("district"), addr.district);
    onChange(f("lat"), addr.lat);
    onChange(f("lng"), addr.lng);
  };

  return (
    <div>
      <Field label="Address Line 1" required>
        <AddressAutocompleteInput
          value={fStr("AddressLine1")}
          onChange={v => onChange(`${prefix}AddressLine1` as keyof WizardState, v)}
          onResolve={handleResolve}
          placeholder="House/Flat/Building number, Street"
          style={inp}
        />
      </Field>
      <Field label="Address Line 2">
        <Input value={fStr("AddressLine2")} onChange={e => onChange(`${prefix}AddressLine2` as keyof WizardState, e.target.value)} placeholder="Area, Locality" />
      </Field>
      <div style={row2}>
        <Field label="Pincode" required>
          <Input value={fStr("Pincode")} onChange={handlePin} placeholder="6-digit pincode" maxLength={6}
            style={{ paddingRight: loading ? 36 : 12 }} />
          {loading && <span style={{ fontSize: 12, color: "var(--color-brand-500)" }}>Looking up…</span>}
        </Field>
        <Field label="City">
          <Input value={fStr("City")} onChange={e => onChange(`${prefix}City` as keyof WizardState, e.target.value)} placeholder="Auto-filled from pincode" />
        </Field>
      </div>
      <div style={row2}>
        <Field label="State">
          <Input value={fStr("State")} onChange={e => onChange(`${prefix}State` as keyof WizardState, e.target.value)} placeholder="Auto-filled from pincode" />
        </Field>
        <Field label="District">
          <Input value={fStr("District")} onChange={e => onChange(`${prefix}District` as keyof WizardState, e.target.value)} placeholder="Auto-filled from pincode" />
        </Field>
      </div>
    </div>
  );
}

function StepAddress({ data, onChange }: { data: WizardState; onChange: (k: keyof WizardState, v: unknown) => void }) {
  const handlePincodeLookup = useCallback(async (pin: string, target: "residential" | "permanent") => {
    const result = await lookupPincode(pin);
    if (result) {
      onChange(`${target}City` as keyof WizardState, result.city);
      onChange(`${target}State` as keyof WizardState, result.state);
      onChange(`${target}District` as keyof WizardState, result.district);
    }
  }, [onChange]);

  const handleSameAs = (checked: boolean) => {
    onChange("sameAsPermanent", checked);
    if (checked) {
      onChange("permanentAddressLine1", data.residentialAddressLine1);
      onChange("permanentAddressLine2", data.residentialAddressLine2);
      onChange("permanentPincode", data.residentialPincode);
      onChange("permanentCity", data.residentialCity);
      onChange("permanentState", data.residentialState);
      onChange("permanentDistrict", data.residentialDistrict);
      onChange("permanentLat", data.residentialLat);
      onChange("permanentLng", data.residentialLng);
    }
  };

  return (
    <div>
      <div style={sectionCard}>
        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Residential Address</h3>
        <AddressBlock prefix="residential" data={data} onChange={onChange} onPincodeLookup={handlePincodeLookup} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, cursor: "pointer", fontWeight: 600 }}>
          <input type="checkbox" checked={data.sameAsPermanent} onChange={e => handleSameAs(e.target.checked)} />
          Permanent address is same as residential address
        </label>
      </div>

      {!data.sameAsPermanent && (
        <div style={sectionCard}>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Permanent Address</h3>
          <AddressBlock prefix="permanent" data={data} onChange={onChange} onPincodeLookup={handlePincodeLookup} />
        </div>
      )}
    </div>
  );
}

function StepEmergency({ data, onChange }: { data: WizardState; onChange: (k: keyof WizardState, v: unknown) => void }) {
  const relationships = useLookup("EMERGENCY_RELATIONSHIP");
  const update = (i: number, field: keyof Contact, val: string | boolean) => {
    // A new name or number is a different call for HR; the server resets it to pending on save.
    const resetsVerification = field === "name" || field === "phone";
    const next = data.contacts.map((c, idx) => {
      if (idx !== i) return c;
      const { verificationStatus: _v, ...rest } = c;
      return resetsVerification ? { ...rest, [field]: val } : { ...c, [field]: val };
    });
    if (field === "isPrimary" && val === true) {
      onChange("contacts", next.map((c, idx) => ({ ...c, isPrimary: idx === i })));
    } else {
      onChange("contacts", next);
    }
  };
  const add = () => onChange("contacts", [...data.contacts, { ...EMPTY_CONTACT }]);
  const remove = (i: number) => {
    if (data.contacts.length <= 2) return;
    onChange("contacts", data.contacts.filter((_, idx) => idx !== i));
  };
  // Flagged on the later of the two matching contacts — the earlier one was
  // entered first, so that is the one to change.
  const duplicateIndex = firstDuplicatePhoneIndex(data.contacts.map(c => c.phone));

  return (
    <div>
      <p style={{ fontSize: 14, color: "var(--color-surface-700)", marginBottom: 16 }}>
        Minimum 2 contacts required. Mark one as primary.
      </p>
      <p className="-mt-2 mb-4 text-xs text-surface-500">
        HR will call each contact to confirm them. Changing a contact's number means HR calls again.
      </p>
      {data.contacts.map((c, i) => (
        <div key={i} style={{ ...sectionCard, position: "relative" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h4 className="flex items-center gap-2" style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>
              Contact {i + 1}
              {c.verificationStatus && <ContactVerificationBadge status={c.verificationStatus} />}
            </h4>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                <input type="radio" name="primary" checked={c.isPrimary} onChange={() => update(i, "isPrimary", true)} />
                Primary
              </label>
              {data.contacts.length > 2 && (
                <button onClick={() => remove(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--color-error)", fontSize: 13 }}>Remove</button>
              )}
            </div>
          </div>
          <div style={row2}>
            <Field label="Name" required><Input value={c.name} onChange={e => update(i, "name", e.target.value)} placeholder="Full name" /></Field>
            <Field label="Relationship" required>
              <Select value={c.relationship} onChange={e => update(i, "relationship", e.target.value)}>
                {relationships.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
              </Select>
            </Field>
          </div>
          <div style={row2}>
            <Field label="Phone" required>
              <PhoneInput value={c.phone} onChange={(v) => update(i, "phone", v)} />
              {duplicateIndex === i && (
                <p className="mt-1 text-xs text-[var(--color-error-fg)]">{DUPLICATE_PHONE_MESSAGE}</p>
              )}
            </Field>
            <Field label="Email"><Input type="email" value={c.email} onChange={e => update(i, "email", e.target.value)} placeholder="Optional" /></Field>
          </div>
        </div>
      ))}
      <button onClick={add} style={{
        display: "flex", alignItems: "center", gap: 6, padding: "8px 16px",
        border: "1.5px dashed var(--color-brand-500)", borderRadius: "var(--radius-md)",
        background: "var(--color-brand-50)", color: "var(--color-brand-600)", cursor: "pointer", fontSize: 14, fontWeight: 600,
      }}>+ Add another contact</button>
    </div>
  );
}

// HRMS-0628: the bank proof upload moved to step 1 (Documents) so the AI prefill
// can land here before the employee first sees the fields. The step now just
// reviews what AI pulled from the cheque / passbook; anything wrong is edited
// here.
function StepBank({ data, onChange, gate }: { data: WizardState; onChange: (k: keyof WizardState, v: unknown) => void; gate: DocGate }) {
  const bankLocked = gate.docStatus["BANK_DOCUMENT"] === "VERIFIED";
  const f = (k: keyof WizardState) => (e: ChangeEvent<HTMLInputElement>) => onChange(k, e.target.value);
  const ifscValid = !data.bankIfscCode || /^[A-Z]{4}0[A-Z0-9]{6}$/.test(data.bankIfscCode);

  return (
    <div>
      <p style={{ fontSize: 13, color: "var(--color-surface-600)", marginBottom: 16 }}>
        We prefilled these from the bank document you uploaded on step 1. Edit anything that looks wrong.
      </p>
      <div style={row2}>
        <Field label="Account Number" required><Input value={data.bankAccountNumber} onChange={f("bankAccountNumber")} placeholder="Bank account number" readOnly={bankLocked} /></Field>
        <Field label="IFSC Code" required>
          <Input value={data.bankIfscCode} onChange={e => onChange("bankIfscCode", e.target.value.toUpperCase())} maxLength={11} placeholder="e.g. HDFC0001234" readOnly={bankLocked}
            style={{ borderColor: ifscValid ? undefined : "var(--color-error)" }} />
          {!ifscValid && <span style={{ fontSize: 12, color: "var(--color-error)" }}>Invalid IFSC format</span>}
        </Field>
      </div>
      <div style={row2}>
        <Field label="Bank Name"><Input value={data.bankName} onChange={f("bankName")} placeholder="e.g. HDFC Bank" readOnly={bankLocked} /></Field>
        <Field label="Branch Name"><Input value={data.bankBranchName} onChange={f("bankBranchName")} placeholder="Branch name" readOnly={bankLocked} /></Field>
      </div>
      <Field label="Account Holder Name" required>
        <Input value={data.bankAccountHolderName} onChange={f("bankAccountHolderName")} placeholder="As printed on cheque" readOnly={bankLocked} />
      </Field>
    </div>
  );
}

// A single upload slot for an educational / statutory document the employee
// supplies on step 5. Reads `docStatus[docType]` to tell "nothing attached"
// from "already uploaded"; on drop, uploads via uploadDocumentOnly and tells
// the parent so the step-advance guard can see the new value.
function EduDocCard({ employeeId, docType, label, required, docStatus, docFile, onUploaded, say }: {
  employeeId: string;
  docType: string;
  label: string;
  required?: boolean;
  docStatus: Record<string, string>;
  /** The file on record, shown inside the field with edit and delete icons. */
  docFile: SlotDoc | undefined;
  onUploaded: () => void;
  say: (line: SayInput) => void;
}) {
  const vhr = useVirtualHr();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const status = docStatus[docType];
  async function handle(file: File) {
    const wrong = wrongFileTypeMessage(file, FILE_RULES.pdfOrImage);
    if (wrong) { setErr(wrong); say(wrongFileTypeLine("pdfOrImage")); return; }
    setBusy(true); setErr("");
    // Sirra off for this document: the file is just stored, with no check and no spoken reply.
    const sirraOn = vhr.isDocEnabled(docType, "onboarding");
    try {
      await uploadDocumentOnly(employeeId, file, docType, { context: "onboarding", skipVerify: !sirraOn });
      onUploaded();
      if (sirraOn) say(docType === "FORM_11" ? FORM11_RECEIVED : docAccepted(docType === "DEGREE" ? "degree" : "marksheet"));
    } catch (e) {
      if (e instanceof SmartBitRejection) {
        setErr(e.message);
        say(rejectionLine(e.verdict.code, { doc: docType === "DEGREE" ? "degree" : docType === "MARKSHEET" ? "marksheet" : undefined, detected: e.verdict.detectedType }));
        return;
      }
      logError("OnboardingWizardPage.EduDocCard", e, { docType });
      say(UPLOAD_FAILED);
      setErr(uploadBlockedMessage(e) ?? "Upload failed. Please try again.");
    } finally { setBusy(false); }
  }
  return (
    <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-surface-200 bg-surface-50 px-3.5 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-[13px] font-semibold">
          <span>{label}</span>
          {status
            ? <Badge tone="success">{status === "VERIFIED" ? "Verified" : "Uploaded"}</Badge>
            : required && <Badge tone="error">Required</Badge>}
        </div>
        {err && <p className="mt-1 text-xs text-[var(--color-error-fg)]">{err}</p>}
      </div>
      <DocumentSlot
        doc={docFile}
        onFile={handle}
        onDeleted={onUploaded}
        label="Upload (PDF or image)"
        accept={FILE_RULES.pdfOrImage.accept}
        loading={busy}
        className="w-full sm:w-72"
      />
    </div>
  );
}

// HRMS-0628: the resume + Education & statutory documents uploads moved to
// step 1 (Documents). This step is now a review-and-edit surface for
// AI-parsed skills, education, work history, and the references editor.
function StepResume({ data, onChange }: {
  data: WizardState;
  onChange: (k: keyof WizardState, v: unknown) => void;
}) {
  const updateEdu = (i: number, k: keyof Education, v: string) => {
    onChange("education", data.education.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  };
  const updateWork = (i: number, k: keyof WorkHistoryItem, v: string | boolean) => {
    onChange("workHistory", data.workHistory.map((w, idx) => idx === i ? { ...w, [k]: v } : w));
  };

  return (
    <div>
      <p style={{ fontSize: 13, color: "var(--color-surface-600)", marginBottom: 16 }}>
        We prefilled skills, education and work history from the resume you uploaded on step 1. Review each row, edit anything we got wrong, and add your references at the bottom.
      </p>

      <div style={sectionCard}>
        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Skills</h3>
        <Field label="Skills (comma-separated)">
          <Input value={data.skills} onChange={e => onChange("skills", e.target.value)} placeholder="e.g. Python, React, Project Management" />
        </Field>
      </div>

      <div style={sectionCard}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Education</h3>
          <button onClick={() => onChange("education", [...data.education, { ...EMPTY_EDU }])}
            style={{ background: "none", border: "1px solid var(--color-brand-500)", borderRadius: "var(--radius-sm)", color: "var(--color-brand-600)", padding: "4px 10px", cursor: "pointer", fontSize: 13 }}>
            + Add
          </button>
        </div>
        {data.education.map((e, i) => (
          <div key={i} style={{ borderTop: i > 0 ? "1px solid var(--color-surface-200)" : "none", paddingTop: i > 0 ? 16 : 0, marginBottom: 16 }}>
            <div style={row2}>
              <Field label="Degree"><Input value={e.degree} onChange={ev => updateEdu(i, "degree", ev.target.value)} placeholder="e.g. B.Tech" /></Field>
              <Field label="Field of Study"><Input value={e.fieldOfStudy} onChange={ev => updateEdu(i, "fieldOfStudy", ev.target.value)} placeholder="e.g. Computer Science" /></Field>
            </div>
            <Field label="Institution"><Input value={e.institution} onChange={ev => updateEdu(i, "institution", ev.target.value)} placeholder="University / College name" /></Field>
            <div style={row2}>
              <Field label="Start Year"><Input type="number" value={e.startYear} onChange={ev => updateEdu(i, "startYear", ev.target.value)} placeholder="2018" min={1980} max={2030} /></Field>
              <Field label="End Year"><Input type="number" value={e.endYear} onChange={ev => updateEdu(i, "endYear", ev.target.value)} placeholder="2022" min={1980} max={2030} /></Field>
            </div>
          </div>
        ))}
      </div>

      <div style={sectionCard}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Work History</h3>
          <button onClick={() => onChange("workHistory", [...data.workHistory, { ...EMPTY_WORK }])}
            style={{ background: "none", border: "1px solid var(--color-brand-500)", borderRadius: "var(--radius-sm)", color: "var(--color-brand-600)", padding: "4px 10px", cursor: "pointer", fontSize: 13 }}>
            + Add
          </button>
        </div>
        {data.workHistory.map((w, i) => (
          <div key={i} style={{ borderTop: i > 0 ? "1px solid var(--color-surface-200)" : "none", paddingTop: i > 0 ? 16 : 0, marginBottom: 16 }}>
            <div style={row2}>
              <Field label="Company"><Input value={w.company} onChange={ev => updateWork(i, "company", ev.target.value)} placeholder="Company name" /></Field>
              <Field label="Job Title"><Input value={w.title} onChange={ev => updateWork(i, "title", ev.target.value)} placeholder="Your role" /></Field>
            </div>
            <div style={row2}>
              <Field label="Start Date"><Input type="month" value={w.startDate} onChange={ev => updateWork(i, "startDate", ev.target.value)} /></Field>
              <Field label="End Date">
                {w.isCurrent ? <span style={{ fontSize: 14, color: "var(--color-success)", fontWeight: 600, lineHeight: "38px" }}>Current role</span>
                  : <Input type="month" value={w.endDate} onChange={ev => updateWork(i, "endDate", ev.target.value)} />}
              </Field>
            </div>
            <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={w.isCurrent} onChange={ev => updateWork(i, "isCurrent", ev.target.checked)} />
              This is my current role
            </label>
          </div>
        ))}
      </div>

      <ReferencesEditor references={data.references} onChange={next => onChange("references", next)} />
    </div>
  );
}

// BGV_REPORT is HR-only: never offered to the employee.
const DEDICATED_DOC_TYPES = new Set(["AADHAAR", "PAN", "OFFER_LETTER", "BANK_DOCUMENT", "OTHER", "BGV_REPORT"]);

function StepReview({ data, employeeId }: { data: WizardState; employeeId: string }) {
  const docTypes = useLookup("DOCUMENT_TYPE");
  const otherDocTypeOptions = docTypes.filter(o => !DEDICATED_DOC_TYPES.has(o.value));

  const [lightboxTarget, setLightboxTarget] = useState<LightboxTarget | null>(null);
  const [docRefreshKey, setDocRefreshKey] = useState(0);
  const [otherDocTypes, setOtherDocTypes] = useState<string[]>([]);
  const [otherType, setOtherType] = useState(otherDocTypeOptions[0]?.value ?? "");
  const [otherFile, setOtherFile] = useState<File | null>(null);
  const [otherUploading, setOtherUploading] = useState(false);
  const [otherMsg, setOtherMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data: docs } = await api.get<Array<{ type: string }>>(`/employees/${employeeId}/documents`);
        if (cancelled) return;
        const distinct = Array.from(new Set(docs.map(d => d.type).filter(t => !DEDICATED_DOC_TYPES.has(t))));
        setOtherDocTypes(distinct);
      } catch (err) { logError("OnboardingWizardPage.StepReview.loadOtherDocs", err); }
    })();
    return () => { cancelled = true; };
  }, [employeeId, docRefreshKey]);

  async function handleAddOtherDoc() {
    if (!otherFile || !otherType) throw new Error("validation");
    setOtherUploading(true); setOtherMsg("");
    try {
      await uploadDocumentOnly(employeeId, otherFile, otherType);
      setOtherMsg("✓ Document uploaded.");
      setOtherFile(null);
      setDocRefreshKey(k => k + 1);
    } catch (err) {
      logError("OnboardingWizardPage.StepReview.addOtherDoc", err);
      setOtherMsg("Upload failed — please try again.");
      throw err;
    } finally { setOtherUploading(false); }
  }

  const row = (label: string, value: ReactNode) => value ? (
    <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 13, color: "var(--color-surface-700)", minWidth: 180 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{value}</span>
    </div>
  ) : null;

  return (
    <div>
      <div style={sectionCard}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Personal Information</h3>
        {row("Title", data.title)}{row("Date of Birth", data.dateOfBirth)}{row("Gender", data.gender)}
        {row("Marital Status", data.maritalStatus)}{row("Spouse", data.spouseName)}
        {row("Personal Email", data.personalEmail)}{row("Personal Phone", data.personalPhone)}
        {row("LinkedIn", data.linkedIn)}{row("T-Shirt Size", data.tshirtSize)}
      </div>
      <div style={sectionCard}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Identity Documents</h3>
        {row("Aadhaar", data.aadhaarNumber ? `XXXX-XXXX-${data.aadhaarNumber.slice(-4)}` : "")}
        {row("PAN", data.panNumber)}
      </div>
      <div style={sectionCard}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Residential Address</h3>
        {row("Address", [data.residentialAddressLine1, data.residentialAddressLine2].filter(Boolean).join(", "))}
        {row("City / State", [data.residentialCity, data.residentialState].filter(Boolean).join(", "))}
        {row("Pincode", data.residentialPincode)}
      </div>
      <div style={sectionCard}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Emergency Contacts</h3>
        {data.contacts.map((c, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            {row(`Contact ${i + 1}`, <>{c.name} ({c.relationship}) — {c.phone}{c.isPrimary && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginLeft: 4 }}><Star size={12} /> Primary</span>}</>)}
          </div>
        ))}
      </div>
      <div style={sectionCard}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Bank Details</h3>
        {row("Bank", data.bankName)}{row("IFSC", data.bankIfscCode)}
        {row("Account", data.bankAccountNumber ? `XXXX${data.bankAccountNumber.slice(-4)}` : "")}
        {row("Account Holder", data.bankAccountHolderName)}
      </div>
      {data.skills && (
        <div style={sectionCard}>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Skills</h3>
          <p style={{ fontSize: 13, margin: 0 }}>{data.skills}</p>
        </div>
      )}

      <div style={sectionCard}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Documents</h3>

        <p style={{ fontSize: 12, fontWeight: 600, color: "var(--color-surface-500)", margin: "0 0 4px", textTransform: "uppercase" }}>Aadhaar Card</p>
        <DocVersionHistory employeeId={employeeId} docType="AADHAAR" isHR={false} refreshKey={docRefreshKey} onVerify={setLightboxTarget} />
        <p style={{ fontSize: 12, fontWeight: 600, color: "var(--color-surface-500)", margin: "16px 0 4px", textTransform: "uppercase" }}>PAN Card</p>
        <DocVersionHistory employeeId={employeeId} docType="PAN" isHR={false} refreshKey={docRefreshKey} onVerify={setLightboxTarget} />
        <p style={{ fontSize: 12, fontWeight: 600, color: "var(--color-surface-500)", margin: "16px 0 4px", textTransform: "uppercase" }}>Offer Letter</p>
        <DocVersionHistory employeeId={employeeId} docType="OFFER_LETTER" isHR={false} refreshKey={docRefreshKey} onVerify={setLightboxTarget} />
        <p style={{ fontSize: 12, fontWeight: 600, color: "var(--color-surface-500)", margin: "16px 0 4px", textTransform: "uppercase" }}>Bank Document</p>
        <DocVersionHistory employeeId={employeeId} docType="BANK_DOCUMENT" isHR={false} refreshKey={docRefreshKey} onVerify={setLightboxTarget} />

        {otherDocTypes.length > 0 && (
          <>
            <p style={{ fontSize: 12, fontWeight: 700, color: "var(--color-surface-600)", margin: "20px 0 8px", textTransform: "uppercase", borderTop: "1px solid var(--color-surface-100)", paddingTop: 16 }}>
              Other Documents
            </p>
            {otherDocTypes.map(t => {
              const label = docTypes.find(o => o.value === t)?.displayName ?? t;
              return (
                <div key={t} style={{ marginBottom: 12 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: "var(--color-surface-500)", margin: "0 0 4px" }}>{label}</p>
                  <DocVersionHistory employeeId={employeeId} docType={t} isHR={false} refreshKey={docRefreshKey} onVerify={setLightboxTarget} />
                </div>
              );
            })}
          </>
        )}

        <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--color-surface-100)" }}>
          <p style={{ fontSize: 13, fontWeight: 600, margin: "0 0 10px" }}>Add another document</p>
          <p style={{ fontSize: 12, color: "var(--color-surface-500)", margin: "0 0 10px" }}>
            e.g. a signed NDA, Employment Agreement, or Anti-Nepotism Policy acknowledgment.
          </p>
          <div style={row2}>
            <Field label="Document Type">
              <Select value={otherType} onChange={e => setOtherType(e.target.value)}>
                {otherDocTypeOptions.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
              </Select>
            </Field>
            <Field label="File">
              <FileInput accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" value={otherFile} onChange={setOtherFile} />
            </Field>
          </div>
          <AsyncButton
            onClick={() => handleAddOtherDoc()}
            disabled={otherUploading || !otherFile || !otherType}
          >
            Upload
          </AsyncButton>
          {otherMsg && <p style={{ fontSize: 13, color: otherMsg.startsWith("✓") ? "green" : "var(--color-warning)", marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>{otherMsg.startsWith("✓") && <CheckCircle2 size={14} />}{otherMsg.replace(/^[✓✕]\s*/, "")}</p>}
        </div>
      </div>

      {lightboxTarget && (
        <DocumentLightbox
          target={lightboxTarget}
          onClose={() => setLightboxTarget(null)}
          onDone={() => { setDocRefreshKey(k => k + 1); setLightboxTarget(null); }}
        />
      )}

      <div style={{ ...sectionCard, background: "var(--color-brand-50)", borderColor: "var(--color-brand-100)" }}>
        <p style={{ margin: 0, fontSize: 14, color: "var(--color-brand-700)", display: "flex", alignItems: "flex-start", gap: 6 }}>
          <CheckCircle2 size={16} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>Review your information above. Once submitted, HR will verify your details and activate your account.
          You can still be contacted to correct any discrepancies.</span>
        </p>
      </div>
    </div>
  );
}

// ─── Step 7: Equipment & Access ───────────────────────────────────────────────

const EQUIP_CATEGORIES = ["LAPTOP","DESKTOP","MONITOR","KEYBOARD","MOUSE","HEADSET",
  "MOBILE","PERIPHERAL","NETWORKING","FURNITURE","SOFTWARE_LICENSE","OTHER"] as const;

const DEPT_KIT: Record<string, string[]> = {
  ENGINEERING: ["LAPTOP","MONITOR","HEADSET"],
  TECHNOLOGY:  ["LAPTOP","MONITOR","HEADSET"],
  SALES:       ["LAPTOP","MOBILE"],
  "BUSINESS DEVELOPMENT": ["LAPTOP","MOBILE"],
  HR:          ["LAPTOP"],
  OPERATIONS:  ["LAPTOP"],
};

// HRMS-0636: the standard IT kit the department's new hires always get. If the
// department doesn't match any preset, falls back to a single laptop — HR can
// extend the IT ticket on their side.
export function defaultKitFor(department: string | undefined): string[] {
  const dept = (department ?? "").toUpperCase();
  return Object.entries(DEPT_KIT).find(([k]) => dept.includes(k))?.[1] ?? ["LAPTOP"];
}

// Human-friendly label for the equipment categories above.
const CATEGORY_LABEL: Record<string, string> = {
  LAPTOP: "Laptop", DESKTOP: "Desktop", MONITOR: "Monitor",
  KEYBOARD: "Keyboard", MOUSE: "Mouse", HEADSET: "Headset",
  MOBILE: "Mobile phone", PERIPHERAL: "Peripheral", NETWORKING: "Networking",
  FURNITURE: "Furniture", SOFTWARE_LICENSE: "Software license", OTHER: "Other",
};

function StepEquipment({ data, onChange, department }: {
  data: WizardState;
  onChange: (k: keyof WizardState, v: unknown) => void;
  department?: string;
}) {
  const items = data.equipmentItems;
  const workLocations = useLookup("WORK_LOCATION");
  function setItems(newItems: EquipmentItem[]) { onChange("equipmentItems", newItems); }

  const defaultKit = defaultKitFor(department);
  const hasMatchedDept = !!department && defaultKit.length > 0 &&
    Object.keys(DEPT_KIT).some(k => (department ?? "").toUpperCase().includes(k));

  return (
    <div>
      {/* HRMS-0636: primary — the standard department kit always goes on submit,
          whether or not the employee opens the optional panel below. */}
      <div style={{ background: "var(--color-brand-50)", border: "1px solid var(--color-brand-100)", borderRadius: "var(--radius-md)", padding: "14px 16px", marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-brand-700)", marginBottom: 6 }}>
          Standard IT kit{department ? ` for ${department}` : ""}
        </div>
        <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--color-brand-700)" }}>
          We&rsquo;ll raise an IT ticket on your behalf for:
        </p>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: "var(--color-brand-700)" }}>
          {defaultKit.map(c => <li key={c} style={{ fontWeight: 600 }}>{CATEGORY_LABEL[c] ?? c}</li>)}
        </ul>
        {!hasMatchedDept && (
          <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--color-brand-700)", fontStyle: "italic" }}>
            We couldn&rsquo;t match your department to a preset, so we&rsquo;ll raise a laptop request. HR can add more on the IT ticket.
          </p>
        )}
      </div>

      {/* Optional add-ons — collapsed by default. */}
      <details style={{ marginBottom: 20, border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", padding: "10px 14px" }}>
        <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--color-surface-700)" }}>
          Need something specific or in addition?
        </summary>
        <p style={{ margin: "10px 0 12px", fontSize: 12, color: "var(--color-surface-500)" }}>
          Anything you add here is appended to the standard kit above. If the category matches a standard item, your specific entry replaces it.
        </p>
        {items.map((item, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "180px 1fr auto", gap: 10, alignItems: "center", marginBottom: 10 }}>
            <SelectField value={item.category} onChange={e => {
              const updated = items.map((it, idx) => idx === i ? { ...it, category: e.target.value } : it);
              setItems(updated);
            }}>
              {EQUIP_CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </SelectField>
            <input value={item.specs} onChange={e => {
              const updated = items.map((it, idx) => idx === i ? { ...it, specs: e.target.value } : it);
              setItems(updated);
            }} placeholder="Preferred specs / model (optional)" style={{ ...inp, margin: 0 }} />
            <button onClick={() => setItems(items.filter((_, idx) => idx !== i))}
              style={{ padding: "6px 10px", border: "1px solid var(--color-error-border)", borderRadius: "var(--radius-md)", background: "transparent", color: "var(--color-error)", cursor: "pointer", fontSize: 13, fontWeight: 700, display: "inline-flex", alignItems: "center" }}>
              <X size={14} />
            </button>
          </div>
        ))}
        <button onClick={() => setItems([...items, { ...EMPTY_EQUIP }])}
          style={{ padding: "6px 14px", border: "1px dashed var(--color-surface-300)", borderRadius: "var(--radius-md)", background: "transparent", color: "var(--color-brand-600)", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
          + Add item
        </button>
      </details>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Needed by date">
          <DatePicker value={data.equipmentStartDate} onChange={v => onChange("equipmentStartDate", v)} />
        </Field>
        <Field label="Office / Location">
          {/* Admin → Dropdown Config "Work Locations"; the IT ticket keeps the display name. */}
          <SelectField value={data.equipmentOfficeLocation} onChange={e => onChange("equipmentOfficeLocation", e.target.value)}>
            <option value="">Select location…</option>
            {/* Keeps a value saved before this was a dropdown visible instead of blanking it. */}
            {data.equipmentOfficeLocation && !workLocations.some(o => o.displayName === data.equipmentOfficeLocation) && (
              <option value={data.equipmentOfficeLocation}>{data.equipmentOfficeLocation}</option>
            )}
            {workLocations.map(o => <option key={o.value} value={o.displayName}>{o.displayName}</option>)}
          </SelectField>
        </Field>
      </div>
    </div>
  );
}

// Step 8: the company policies to sign (handbook and leave policy today; any
// document the policy-signing API adds later shows up here too), then the
// signed copies to download.
function StepSignDocuments({ status, onOpened, onSign }: {
  status: PolicySigningStatus;
  onOpened: (kind: PolicyKind) => void;
  onSign: (kind: PolicyKind) => Promise<unknown>;
}) {
  if (!status.required && status.signed.length === 0) {
    return <p className="text-sm text-surface-600">There are no documents to sign right now. Continue to review and submit.</p>;
  }
  return (
    <div className="space-y-5">
      <p className="text-[13px] text-surface-600">
        {status.required
          ? "HR has asked you to sign these. Read each one to the end, tick the box, and sign."
          : "All signed. Download a copy of each for your records."}
      </p>
      {status.required && <PolicySignPanel outstanding={status.outstanding} signerName={status.signerName} onOpened={onOpened} onSign={onSign} />}
      {status.signed.length > 0 && (
        <Card className="bg-surface-50">
          <CardHeader className="pb-3"><CardTitle className="text-[15px]">Signed documents</CardTitle></CardHeader>
          <CardContent><SignedPolicyDownloads signed={status.signed} /></CardContent>
        </Card>
      )}
    </div>
  );
}

// What the employee sees once they have submitted: everything they sent, read
// only, with each document's review status. Replaces the old redirect to
// /onboarding, which an onboarding-link session cannot open at all.
function SubmittedView({ completed, submittedAt, docStatus, signedPolicies }: {
  completed: boolean;
  submittedAt: string | null;
  docStatus: Record<string, string>;
  signedPolicies: SignedPolicy[];
}) {
  const DOCS: Array<{ type: string; label: string }> = [
    { type: "OFFER_LETTER", label: "Offer letter" },
    { type: "AADHAAR", label: "Aadhaar" },
    { type: "PAN", label: "PAN" },
    { type: "BANK_DOCUMENT", label: "Bank document" },
    { type: "OTHER", label: "Resume" },
  ];
  const docTone = (s: string | undefined) => s === "VERIFIED" ? "success" : s === "REJECTED" ? "error" : s ? "warning" : "neutral";
  const fmt = (iso: string) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  return (
    <div className="min-h-screen bg-surface-50 py-10">
      <div className="mx-auto max-w-3xl space-y-5 px-4">
        <Card className={completed ? "border-[var(--color-success-border)] bg-[var(--color-success-bg-subtle)]" : "border-[var(--color-info-border)] bg-[var(--color-info-bg-subtle)]"}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 size={18} /> {completed ? "Your onboarding is complete" : "Submitted — awaiting HR review"}
            </CardTitle>
            <CardDescription>
              {completed
                ? "Welcome aboard. Sign in with your work account to use the HRMS."
                : <>Thanks{submittedAt && <> — we received your details on {fmt(submittedAt)}</>}. HR is reviewing them now. If anything needs changing, HR will email you a link to update it.</>}
            </CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader><CardTitle>Documents</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {DOCS.filter(d => docStatus[d.type]).map(d => (
              <div key={d.type} className="flex flex-wrap items-center gap-2 py-0.5">
                <Badge tone={docTone(docStatus[d.type])}>{(docStatus[d.type] ?? "").replace(/_/g, " ").toLowerCase()}</Badge>
                <span className="text-[13px]">{d.label}</span>
              </div>
            ))}
            {!DOCS.some(d => docStatus[d.type]) && <p className="text-[13px] text-surface-500">No documents on record.</p>}
          </CardContent>
        </Card>

        {signedPolicies.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Signed policies</CardTitle></CardHeader>
            <CardContent><SignedPolicyDownloads signed={signedPolicies} /></CardContent>
          </Card>
        )}

        {!completed && (
          <Card>
            <CardHeader><CardTitle>What happens next</CardTitle></CardHeader>
            <CardContent className="space-y-1 text-[13px]">
              <p>HR will verify your documents, call your emergency contacts and check your references.</p>
              <p>You&apos;ll get an email at your personal address once your onboarding is complete.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ─── Main Wizard Page ─────────────────────────────────────────────────────────

// HRMS-0628: the wizard now opens with a single Documents step. Everything
// AI-extractable (Aadhaar, PAN, Offer Letter, Bank proof, Resume + the India
// Degree/Marksheet/Form 11) is attached there up-front, and the later steps
// are review/edit of fields the AI already filled in.
const STEPS = [
  "Documents", "Personal Info", "Address",
  "Emergency Contacts", "Bank Details", "References & History",
  "Equipment & Access",
];

// After the saved data steps: signing the company policies (handbook, leave
// policy, and any document added later), then the final Review & Submit screen.
// Neither is saved through the wizard/:step endpoint.
const SIGN_STEP = STEPS.length;
const REVIEW_STEP = STEPS.length + 1;
const STEPPER_LABELS = [...STEPS, "Sign Documents", "Review"];

// Sirra's line for a wizard step. The sign step has its own line; the review
// step keeps the script's last line (and its recording).
const sirraLine = (step: number) =>
  step === SIGN_STEP ? SIGN_DOCUMENTS_LINE : stepLine(step > SIGN_STEP ? step - 1 : step);

export default function OnboardingWizardPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const token = getAccessToken() ?? "";
  const { employeeId } = decodeToken(token);

  const [step, setStep] = useState(0);
  // Step just saved, so the stepper plays its completion animation for that one only.
  const [justCompleted, setJustCompleted] = useState<number | null>(null);
  const [state, setState] = useState<WizardState>(INITIAL);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [employeeDept, setEmployeeDept] = useState<string | undefined>();
  // Handbook and leave policy: what is still to sign, and the signed copies to download.
  const [policyStatus, setPolicyStatus] = useState<PolicySigningStatus>(EMPTY_POLICY_STATUS);
  const [recordMissing, setRecordMissing] = useState(false);
  const [dataConsentAt, setDataConsentAt] = useState<string | null>(null);
  const [docStatus, setDocStatus] = useState<Record<string, string>>({});
  const [docFiles, setDocFiles] = useState<Record<string, SlotDoc>>({});
  const [submitted, setSubmitted] = useState<{ status: string; at: string | null } | null>(null);
  // True once the saved step is known, so Sirra opens on the right step instead of step 1 first.
  const [statusLoaded, setStatusLoaded] = useState(false);

  const refreshDocStatus = useCallback(() => {
    api.get<Array<{ id: string; name: string; type: string; status: string; mimeType: string | null }>>(`/employees/${employeeId}/documents`)
      .then(r => {
        setDocStatus(Object.fromEntries(r.data.map(d => [d.type, d.status])));
        setDocFiles(Object.fromEntries(r.data.map(d => [d.type, { id: d.id, name: d.name, status: d.status, mimeType: d.mimeType }])));
      })
      .catch((err) => logError("OnboardingWizardPage.loadDocStatus", err));
  }, [employeeId]);

  const loadPolicyStatus = useCallback(() =>
    api.get<PolicySigningStatus>(`/employees/${employeeId}/handbook/status?scope=onboarding`)
      .then(r => setPolicyStatus(r.data))
      .catch((err) => logError("OnboardingWizardPage.loadPolicyStatus", err)),
  [employeeId]);

  const guide = useVirtualHrGuide();
  // The top bar's height changes as the stepper wraps, so it is measured (a runtime value Tailwind
  // cannot express) and the pinned guide card sits just below it via --wizard-bar-h.
  const barObserver = useRef<ResizeObserver | null>(null);
  const topBarRef = useCallback((el: HTMLDivElement | null) => {
    barObserver.current?.disconnect();
    barObserver.current = null;
    if (!el?.parentElement) return;
    const root = el.parentElement;
    const measure = () => root.style.setProperty("--wizard-bar-h", `${el.offsetHeight}px`);
    measure();
    barObserver.current = new ResizeObserver(measure);
    barObserver.current.observe(el);
  }, []);
  const vhr = useVirtualHr();
  // The admin's Onboarding switch (with the master switch): off means plain uploads and no Sirra at all.
  const sirraOn = vhr.contextEnabled("onboarding");
  const gate: DocGate = {
    consentAt: dataConsentAt, onConsented: setDataConsentAt, docStatus, docFiles, onUploaded: refreshDocStatus,
    say: sirraOn ? guide.say : NOTHING, busy: sirraOn ? guide.setBusy : NOTHING,
  };
  useEffect(() => { if (!sirraOn) guide.stop(); }, [sirraOn, guide.stop]);

  useEffect(() => {
    refreshDocStatus();
    api.get<{ wizardStep: number; status: string; dataConsentAt: string | null; submittedForReviewAt: string | null }>(`/employees/${employeeId}/onboarding`)
      .then(r => {
        setDataConsentAt(r.data.dataConsentAt);
        // Submitted or completed: the wizard is read-only (the server refuses
        // edits too). The old redirect sent them to a page a link session
        // cannot open at all.
        if (r.data.status === "PENDING_HR_REVIEW" || r.data.status === "COMPLETED") {
          setSubmitted({ status: r.data.status, at: r.data.submittedForReviewAt });
        } else {
          setSubmitted(null);
          // wizardStep counts the data steps saved; all saved lands on Sign Documents.
          setStep(Math.max(0, Math.min(r.data.wizardStep, SIGN_STEP)));
        }
        setStatusLoaded(true);
      })
      .catch((err) => {
        if ((err as { response?: { status?: number } })?.response?.status === 404) {
          setRecordMissing(true);
        }
        logError("OnboardingWizardPage.loadStatus", err);
        setStatusLoaded(true);
      });

    // Nothing published yet means nothing to sign -- onboarding is not blocked on it.
    void loadPolicyStatus();

    // Hydrate with anything already saved (by a previous session, or by HR's
    // resume prefill at creation time) instead of always starting blank.
    api.get<Partial<WizardState>>(`/employees/${employeeId}/onboarding/wizard-state`)
      .then(r => {
        const fetched = r.data;
        setState(prev => ({
          ...prev,
          ...fetched,
          contacts: fetched.contacts?.length ? fetched.contacts : prev.contacts,
          education: fetched.education?.length ? fetched.education : prev.education,
          workHistory: fetched.workHistory?.length ? fetched.workHistory : prev.workHistory,
          references: fetched.references?.length ? withReferenceSlots(fetched.references) : prev.references,
        }));
      })
      .catch((err) => logError("OnboardingWizardPage.loadWizardState", err));

    api.get<{ department?: { name: string } | null }>(`/employees/${employeeId}`)
      .then(r => { if (r.data.department?.name) setEmployeeDept(r.data.department.name); })
      .catch((err) => logError("OnboardingWizardPage.loadEmployeeDept", err));
  }, [employeeId, navigate, refreshDocStatus, loadPolicyStatus]);

  // Sirra introduces herself once, then talks through each step as it opens: moving to a step in
  // this visit always speaks it. When the page loads (or is refreshed) she speaks only what this
  // tab has not heard yet; a step already heard opens with her line shown and silent, and
  // Replay speaks the intro and the step from the start.
  const introduced = useRef(false);
  const [heardAtLoad] = useState(() => readHeard(employeeId));
  // The lines just said, saved as heard once her voice actually starts (so a refresh before the
  // first click still plays them).
  const awaitingVoice = useRef<string[]>([]);
  useEffect(() => {
    if (!sirraOn || !statusLoaded || submitted || recordMissing) return;
    const timer = window.setTimeout(() => {
      const line = sirraLine(step);
      const stepKey = `step${step}`;
      const pageLoad = !introduced.current;
      introduced.current = true;
      if (pageLoad && heardAtLoad.has("intro") && heardAtLoad.has(stepKey)) { guide.show([INTRO, line]); return; }
      awaitingVoice.current = pageLoad && !heardAtLoad.has("intro") ? ["intro", stepKey] : [stepKey];
      guide.say(pageLoad && !heardAtLoad.has("intro") ? [INTRO, line] : line);
    }, 700);
    return () => window.clearTimeout(timer);
  }, [step, sirraOn, statusLoaded, submitted, recordMissing, heardAtLoad, guide.say, guide.show]);
  useEffect(() => {
    if (!guide.speaking || !awaitingVoice.current.length) return;
    writeHeard(employeeId, awaitingVoice.current);
    awaitingVoice.current = [];
  }, [guide.speaking, employeeId]);
  // Fetch her recordings before they are needed, so the voice starts at once even on a slow connection:
  // what she is about to say now, then (a moment later, so it does not compete with it) the next step's line
  // and, on the Documents step, what she says about an upload.
  useEffect(() => {
    if (!sirraOn || !statusLoaded || submitted || recordMissing) return;
    guide.prefetch([INTRO, sirraLine(step)]);
    const later = window.setTimeout(() => {
      guide.prefetch([sirraLine(step + 1)]);
      if (step === 0) guide.prefetch(UPLOAD_RESULT_LINES);
    }, 1500);
    return () => window.clearTimeout(later);
  }, [step, sirraOn, statusLoaded, submitted, recordMissing, guide.prefetch]);

  const onChange = useCallback((k: keyof WizardState, v: unknown) => {
    setState(prev => ({ ...prev, [k]: v }));
  }, []);

  async function handleSaveStep() {
    setSaving(true); setError("");
    try {
      if (step === 0) {
        // HRMS-0628: Degree and Marksheet are uploaded on step 1 (Documents)
        // now. "country" is the employee's office country upper-cased by
        // the server; the server keys the submit gate on `!== "US"` too, so
        // the dev DB's "INDIA" and production's "IN" both take the same
        // path.
        if (state.country && state.country !== "US") {
          const missing: string[] = [];
          if (!docStatus["DEGREE"])    missing.push("degree certificate");
          if (!docStatus["MARKSHEET"]) missing.push("marksheet");
          if (missing.length) {
            const msg = `Upload your ${missing.join(" and ")} on this step before continuing.`;
            setError(msg); toast.show(msg, "error"); throw new Error(VALIDATION_HANDLED);
          }
        }
      }
      if (step === 3) {
        const phoneError = validateEmergencyContactPhones(state.contacts.map(c => c.phone));
        if (phoneError) { setError(phoneError); toast.show(phoneError, "error"); throw new Error(VALIDATION_HANDLED); }
      }
      if (step === 5) {
        const refError = validateReferences(state.references);
        if (refError) { setError(refError); toast.show(refError, "error"); throw new Error(VALIDATION_HANDLED); }
      }
      const stepNum = step + 1;
      const payload = buildStepPayload(step, state, employeeDept);
      await api.patch(`/employees/${employeeId}/onboarding/wizard/${stepNum}`, payload);
      setJustCompleted(step);
      setStep(prev => prev + 1);
    } catch (e: unknown) {
      // A step validation failure already set the banner and the toast with the
      // actual reason. errMsg() only reads an axios response, so letting these
      // fall through replaced that reason with "Failed to save. Please try again."
      if (e instanceof Error && e.message === VALIDATION_HANDLED) throw e;
      const msg = errMsg(e);
      setError(msg ?? "Failed to save. Please try again.");
      toast.show(msg ?? "Failed to save. Please try again.", "error");
      throw e;
    } finally { setSaving(false); }
  }

  // HRMS-0636: handleSkipEquipment removed. The equipment step no longer has a
  // skip path from the wizard — the department's standard IT kit always goes.
  // HR can trim the resulting IT ticket on the IT side for the rare hire who
  // genuinely needs no equipment.

  // Records when the handbook was first opened (the activation gate checks it).
  function markPolicyOpened(kind: PolicyKind) {
    if (kind !== "HANDBOOK") return;
    api.post(`/employees/${employeeId}/handbook/acknowledge`, { opened: true })
      .catch((err) => logError("OnboardingWizardPage.markHandbookOpened", err));
  }

  // Signs one document with the employee's name on record.
  async function signPolicy(kind: PolicyKind) {
    setError("");
    try {
      const { data } = await api.post<PolicySigningStatus>(`/employees/${employeeId}/policies/${encodeURIComponent(kind)}/sign?scope=onboarding`, {});
      setPolicyStatus(data);
    } catch (e: unknown) {
      setError(errMsg(e) ?? "Failed to record your signature");
      throw e;
    }
  }

  async function handleSubmit() {
    setSubmitting(true); setError("");
    try {
      await api.post(`/employees/${employeeId}/onboarding/submit-for-review`, {});
      setSubmitted({ status: "PENDING_HR_REVIEW", at: new Date().toISOString() });
      window.scrollTo({ top: 0 });
    } catch (e: unknown) {
      const msg = errMsg(e);
      setError(msg ?? "Failed to submit. Please try again.");
      throw e;
    } finally { setSubmitting(false); }
  }

  const isSignStep = step === SIGN_STEP;
  const isLastStep = step === REVIEW_STEP;

  if (recordMissing) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--color-surface-50)", fontFamily: "var(--font-display)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
        <div style={{ background: "var(--color-surface-0)", border: "1px solid var(--color-surface-200)", borderRadius: 12, padding: 32, maxWidth: 480, textAlign: "center" }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Onboarding not yet set up</h1>
          <p style={{ fontSize: 14, color: "var(--color-surface-700)", lineHeight: 1.5 }}>
            Your onboarding record hasn't been initiated yet. This is normally started by HR
            before you begin — please contact HR to have it set up, then come back to this page.
          </p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <SubmittedView
        completed={submitted.status === "COMPLETED"}
        submittedAt={submitted.at}
        docStatus={docStatus}
        signedPolicies={policyStatus.signed}
      />
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--color-surface-50)", fontFamily: "var(--font-display)" }}>
      {/* One top bar: title on the left, stepper filling the rest of the row.
          Sticky so the step-completion animation stays in view while saving
          from the bottom of a long step. */}
      <div ref={topBarRef} className="sticky top-0 z-20 flex flex-col gap-3 border-b border-surface-200 bg-surface-0 px-6 py-3 md:flex-row md:items-center md:gap-8">
        <div className="flex shrink-0 items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-lg bg-brand-600 text-white">
            <Sparkle size={16} />
          </div>
          <div>
            <h1 className="text-[17px] font-bold leading-tight">Employee Onboarding</h1>
            <p className="text-[13px] text-surface-700">Complete your profile to get started</p>
          </div>
        </div>
        <OnboardingStepper
          steps={STEPPER_LABELS}
          current={step}
          justCompleted={justCompleted}
          onSelect={(i) => { setJustCompleted(null); setStep(i); }}
        />
      </div>

      {/* Content */}
      <div style={{ maxWidth: 944, margin: "16px auto 32px", padding: "0 16px" }}>
        <VirtualHrGuide guide={guide} />
        {error && (
          <div style={{ background: "var(--color-error-bg)", border: "1px solid var(--color-error-border)", borderRadius: "var(--radius-md)", padding: "12px 16px", marginBottom: 16, fontSize: 14, color: "var(--color-error-fg)" }}>
            {error}
          </div>
        )}

        <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-xl)", border: "1px solid var(--color-surface-200)", padding: 32, boxShadow: "var(--shadow-md)" }}>
          <h2 style={{ margin: "0 0 24px", fontSize: 20, fontWeight: 700 }}>
            {isLastStep ? "Review & Submit" : `Step ${step + 1}: ${STEPPER_LABELS[step]}`}
          </h2>

          {step === 0 && <StepDocuments data={state} onChange={onChange} employeeId={employeeId} gate={gate} />}
          {step === 1 && <StepPersonal data={state} onChange={onChange} gate={gate} />}
          {step === 2 && <StepAddress data={state} onChange={onChange} />}
          {step === 3 && <StepEmergency data={state} onChange={onChange} />}
          {step === 4 && <StepBank data={state} onChange={onChange} gate={gate} />}
          {step === 5 && <StepResume data={state} onChange={onChange} />}
          {step === 6 && <StepEquipment data={state} onChange={onChange} {...(employeeDept ? { department: employeeDept } : {})} />}
          {isSignStep && (
            <StepSignDocuments status={policyStatus} onOpened={markPolicyOpened} onSign={signPolicy} />
          )}
          {isLastStep && <StepReview data={state} employeeId={employeeId} />}
          {isLastStep && policyStatus.signed.length > 0 && (
            <Card className="mt-5 bg-surface-50">
              <CardHeader className="pb-3"><CardTitle className="text-[15px]">Signed documents</CardTitle></CardHeader>
              <CardContent><SignedPolicyDownloads signed={policyStatus.signed} /></CardContent>
            </Card>
          )}
        </div>

        {/* HRMS-0629: the server already saves progress on each Save & Continue
            and HR's resend-link preserves wizardStep — surface that so the
            employee knows they can walk away mid-wizard. */}
        {!isLastStep && (
          <p style={{ margin: "16px 2px 0", fontSize: 12, color: "var(--color-surface-500)" }}>
            Progress is saved on each Save &amp; Continue. You can close the tab and come back — HR can resend the link if your current one expires.
          </p>
        )}

        {/* HR-only note */}
        <div style={hrCard}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <Lock size={14} />
            <span style={{ fontSize: 13, fontWeight: 700, color: "var(--color-surface-700)" }}>HR Information</span>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: "var(--color-surface-700)" }}>
            Fields like ESI number, UAN, salary, and appraisal dates are managed by HR and will be filled by your HR team after your profile is submitted.
          </p>
        </div>

        {/* Navigation */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 24 }}>
          <button
            onClick={() => { setJustCompleted(null); setStep(prev => Math.max(0, prev - 1)); }}
            disabled={step === 0}
            style={{
              padding: "10px 24px", borderRadius: "var(--radius-md)", border: "1.5px solid var(--color-surface-200)",
              background: "var(--color-surface-0)", cursor: step === 0 ? "not-allowed" : "pointer", fontSize: 14, fontWeight: 600,
              color: step === 0 ? "var(--color-surface-300)" : "var(--color-surface-700)",
            }}>
            ← Back
          </button>

          {/* HRMS-0636: Skip-this-step on Equipment is gone — the default IT
              kit always goes. The regular Save & Continue below carries it. */}

          {isLastStep ? (
            <AsyncButton
              onClick={() => handleSubmit()}
              disabled={submitting || policyStatus.required}
              title={policyStatus.required ? "Sign the documents on step 8 first" : undefined}
            >
              Submit for HR Review →
            </AsyncButton>
          ) : isSignStep ? (
            // Nothing to save here: signing already happened on this step.
            <SpecularButton
              onClick={() => { setJustCompleted(step); setStep(REVIEW_STEP); }}
              disabled={policyStatus.required}
              title={policyStatus.required ? "Sign the documents above to continue" : undefined}
            >
              Continue →
            </SpecularButton>
          ) : (
            <AsyncButton
              onClick={() => handleSaveStep()}
              disabled={saving}
            >
              Save & Continue →
            </AsyncButton>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Build step payload ───────────────────────────────────────────────────────

export function buildStepPayload(step: number, s: WizardState, employeeDept?: string): Record<string, unknown> {
  // HRMS-0628: step 0 (Documents) carries every field the AI extracted while
  // the employee was dropping files. Any field the user then edits on a
  // later review step is sent again by that step's own branch; the server
  // writes are last-wins, so a late edit still overrides what AI pulled.
  if (step === 0) return {
    panNumber: s.panNumber || null, aadhaarNumber: s.aadhaarNumber || null,
    ...(s.firstName ? { firstName: s.firstName } : {}),
    ...(s.middleName ? { middleName: s.middleName } : {}),
    ...(s.lastName ? { lastName: s.lastName } : {}),
    // Date of birth, gender and address come off the Aadhaar, contact details off the résumé: saved with
    // the documents so they survive a refresh before the employee reaches those steps.
    dateOfBirth: s.dateOfBirth || null, gender: s.gender || null,
    personalEmail: s.personalEmail || null, personalPhone: s.personalPhone || null, linkedIn: s.linkedIn || null,
    residentialAddress: [s.residentialAddressLine1, s.residentialAddressLine2].filter(Boolean).join("\n") || null,
    residentialPincode: s.residentialPincode || null,
    residentialCity: s.residentialCity || null, residentialState: s.residentialState || null, residentialDistrict: s.residentialDistrict || null,
    bankAccountNumber: s.bankAccountNumber || null,
    bankIfscCode: s.bankIfscCode || null, bankName: s.bankName || null,
    bankBranchName: s.bankBranchName || null, bankAccountHolderName: s.bankAccountHolderName || null,
    skills: s.skills || null,
    education: s.education.filter(e => e.degree && e.institution).map(e => ({
      degree: e.degree, institution: e.institution, fieldOfStudy: e.fieldOfStudy || null,
      startYear: e.startYear ? parseInt(e.startYear) : null,
      endYear: e.endYear ? parseInt(e.endYear) : null,
    })),
    workHistory: s.workHistory.filter(w => w.company && w.title).map(w => ({
      company: w.company, title: w.title,
      startDate: w.startDate || null, endDate: w.isCurrent ? null : (w.endDate || null),
      isCurrent: w.isCurrent,
    })),
  };
  if (step === 1) return {
    title: s.title, dateOfBirth: s.dateOfBirth || null, gender: s.gender,
    maritalStatus: s.maritalStatus, spouseName: s.spouseName || null,
    anniversaryDate: s.anniversaryDate || null,
    personalEmail: s.personalEmail || null, personalPhone: s.personalPhone || null,
    linkedIn: s.linkedIn || null, tshirtSize: s.tshirtSize || null,
    // PAN/Aadhaar/name are also editable on Personal Info; include them so a
    // late correction here reaches the server too.
    panNumber: s.panNumber || null, aadhaarNumber: s.aadhaarNumber || null,
    ...(s.firstName ? { firstName: s.firstName } : {}),
    ...(s.middleName ? { middleName: s.middleName } : {}),
    ...(s.lastName ? { lastName: s.lastName } : {}),
  };
  if (step === 2) {
    const resAddr = [s.residentialAddressLine1, s.residentialAddressLine2].filter(Boolean).join("\n");
    const permAddr = s.sameAsPermanent ? resAddr
      : [s.permanentAddressLine1, s.permanentAddressLine2].filter(Boolean).join("\n");
    return {
      residentialAddress: resAddr || null,
      residentialPincode: s.residentialPincode || null,
      residentialCity: s.residentialCity || null,
      residentialState: s.residentialState || null,
      residentialDistrict: s.residentialDistrict || null,
      residentialLat: s.residentialLat,
      residentialLng: s.residentialLng,
      permanentAddress: permAddr || null,
      permanentPincode: s.sameAsPermanent ? s.residentialPincode : (s.permanentPincode || null),
      permanentCity: (s.sameAsPermanent ? s.residentialCity : s.permanentCity) || null,
      permanentState: (s.sameAsPermanent ? s.residentialState : s.permanentState) || null,
      permanentDistrict: (s.sameAsPermanent ? s.residentialDistrict : s.permanentDistrict) || null,
      permanentLat: s.sameAsPermanent ? s.residentialLat : s.permanentLat,
      permanentLng: s.sameAsPermanent ? s.residentialLng : s.permanentLng,
    };
  }
  if (step === 3) return { contacts: s.contacts.filter(c => c.name && c.phone) };
  if (step === 4) return {
    bankAccountNumber: s.bankAccountNumber || null,
    bankIfscCode: s.bankIfscCode || null, bankName: s.bankName || null,
    bankBranchName: s.bankBranchName || null, bankAccountHolderName: s.bankAccountHolderName || null,
  };
  if (step === 5) return {
    skills: s.skills || null,
    references: referencesPayload(s.references),
    education: s.education.filter(e => e.degree && e.institution).map(e => ({
      degree: e.degree, institution: e.institution, fieldOfStudy: e.fieldOfStudy || null,
      startYear: e.startYear ? parseInt(e.startYear) : null,
      endYear: e.endYear ? parseInt(e.endYear) : null,
    })),
    workHistory: s.workHistory.filter(w => w.company && w.title).map(w => ({
      company: w.company, title: w.title,
      startDate: w.startDate || null, endDate: w.isCurrent ? null : (w.endDate || null),
      isCurrent: w.isCurrent,
    })),
  };
  if (step === 6) {
    // HRMS-0636: the department's standard IT kit always goes on submit. Any
    // specific items the employee added in the optional panel are merged in by
    // category: a specific entry (with its own specs) wins over the plain
    // default row of the same category, and other new categories append.
    const defaults = defaultKitFor(employeeDept).map(c => ({ category: c, specs: null as string | null }));
    const additions = s.equipmentItems
      .filter(i => i.category)
      .map(i => ({ category: i.category, specs: i.specs || null }));
    const byCategory = new Map<string, { category: string; specs: string | null }>();
    for (const d of defaults)  byCategory.set(d.category, d);
    for (const a of additions) byCategory.set(a.category, a);
    return {
      requestedAssets: Array.from(byCategory.values()),
      startDate:       s.equipmentStartDate     || null,
      officeLocation:  s.equipmentOfficeLocation || null,
      skipped:         false,
    };
  }
  return {};
}
